// PlayCanvas app bootstrap — single source of truth for the renderer + camera.

import * as pc from 'playcanvas';

export interface CreateAppResult {
  app: pc.Application;
  camera: pc.Entity;
  detach: () => void;
}

export interface CreateAppOptions {
  /** Vertical FOV (degrees) for the perspective camera. */
  fov?: number;
  /** Background colour while textures are loading. */
  clearColor?: [number, number, number, number];
}

export function createApp(canvas: HTMLCanvasElement, opts: CreateAppOptions = {}): CreateAppResult {
  const app = new pc.Application(canvas, {
    mouse: new pc.Mouse(canvas),
    touch: new pc.TouchDevice(canvas),
    keyboard: new pc.Keyboard(window),
    gamepads: new pc.GamePads(),
    graphicsDeviceOptions: {
      alpha: false,
      antialias: true,
      // preserveDrawingBuffer is required so the variant-comparison feature
      // can call canvas.toDataURL() after a render() to snapshot the current
      // view. Without it, browsers wipe the backbuffer after compositing,
      // returning a blank PNG. Perf cost is negligible for a non-streaming
      // 360° viewer (a few MB extra GPU memory).
      preserveDrawingBuffer: true,
      powerPreference: 'high-performance',
    },
  });

  app.setCanvasFillMode(pc.FILLMODE_FILL_WINDOW);
  app.setCanvasResolution(pc.RESOLUTION_AUTO);

  // Cap pixel ratio on mobile for perf — full DPI on desktop.
  const dpr = Math.min(window.devicePixelRatio || 1, isMobileViewport() ? 1.5 : 2);
  app.graphicsDevice.maxPixelRatio = dpr;

  const camera = new pc.Entity('camera');
  camera.addComponent('camera', {
    clearColor: new pc.Color(...(opts.clearColor ?? [0, 0, 0, 1])),
    fov: opts.fov ?? 72,
    nearClip: 0.05,
    farClip: 1000,
    projection: pc.PROJECTION_PERSPECTIVE,
  });
  camera.setLocalPosition(0, 0, 0);
  app.root.addChild(camera);

  // Resize handling — PlayCanvas handles canvas autoResize when fill mode is set,
  // but we still need to nudge it on mobile rotate.
  const onResize = (): void => {
    app.resizeCanvas();
  };
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);

  app.start();

  return {
    app,
    camera,
    detach: () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      app.destroy();
    },
  };
}

function isMobileViewport(): boolean {
  return Math.min(window.innerWidth, window.innerHeight) < 720;
}
